import { nonnull } from "@tento-core/assert";
import "@tento-ui/components/input/dropdown-input";

import { Actor, Json, type Option, type Timescale } from "@tento-lona/sheets";
import type { JsonObject } from "@tento-lona/sheets";
import { type LonaSdk, type SheetRow, type WeatherLocation, WeatherLocations } from "@tento-lona";
import { ParentStoredRow } from "@tento-lona/platform";
import { COLUMN_WEATHER_SPEC, AliasRow, SourceRow, ColumnWeatherRow } from "@tento-lona/sheets/platform";
import {
  HomeCardFactory,
  type BindCellContext,
  type RenderPlugin,
  RowOptionsElement,
  SheetLegend,
  DefaultLegendLayout,
  type TextMeasurer,
} from "@tento-lona/sheets-ui";
import type { SheetElement } from "@tento-lona/sheets-ui/sheet/sheet-element.ts";
import { NaiveDate } from "@tento-chrono";
import { Tracing } from "@tento-core/tracing";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { WeatherCell } from "@tento-ui/components/weather/weather-cell";
import { WeatherTooltip } from "@tento-ui/components/weather/weather-tooltip";
import { CloudIcon, LonaIcons } from "@tento-ui/ui/icons";
import type { PerTypeSpec } from "@tento-lona/sheets/platform";
import type { SheetLegendClass } from "@tento-lona/sheets-ui/sheet/render-plugin.ts";
import { liveVariantOf } from "../../sheet/live-variant.ts";

/** Weather cell data shape (matches Rust WeatherData serialization). */
interface WeatherCellData {
  date: { yr: number; month1: number; day1: number };
  day: WeatherTooltip.Day;
}

/**
 * Resolve a Joined.Handle to its ColumnWeatherRow child. Used outside
 * the bindCell dispatch (homeCard, legend, rowOptions, sheet-property-row)
 * where the infra-guaranteed typed renderer isn't threaded directly.
 */
function weatherRendererOf(
  row: SheetRow.Joined.Handle,
): Option<ColumnWeatherRow> {
  const variant = liveVariantOf(row);
  // A weather renderer drawn as a row of its own (a section or inert chrome contains it).
  if (variant instanceof ColumnWeatherRow) return variant;
  if (!(variant instanceof SourceRow || variant instanceof AliasRow)) {
    return null;
  }
  return weatherRendererIn(variant);
}

function weatherRendererIn(row: SourceRow | AliasRow): Option<ColumnWeatherRow> {
  if (row instanceof SourceRow) {
    const renderer = row.defaultRenderer(ColumnWeatherRow);
    if (renderer) return renderer;
  }

  for (const child of row.children) {
    if (child instanceof ColumnWeatherRow) return child;
    if (child instanceof SourceRow || child instanceof AliasRow) {
      const renderer = weatherRendererIn(child);
      if (renderer) return renderer;
    }
  }
  return null;
}

/**
 * The location `row` is configured for: its weather renderer's (`lid`, which
 * follows the binding); for a weather alias with no renderer left, the
 * alias's own `locationId` prop, the input its template builds that renderer
 * from. `null` when neither names one.
 */
function weatherLocationIdOf(row: SheetRow.Joined.Handle): Option<number> {
  const renderer = weatherRendererOf(row);
  if (renderer != null) return renderer.lid;
  const variant = liveVariantOf(row);
  if (!(variant instanceof AliasRow)) return null;
  const prop = Json.string(variant.instance().props, "locationId");
  const lid = prop == null ? NaN : Number(prop);
  return Number.isFinite(lid) && String(lid) === prop ? lid : null;
}

/** The `keys` of `obj` it holds, copied; a key it lacks is left out. */
function presentKeys(obj: JsonObject, keys: readonly string[]): JsonObject {
  return Object.fromEntries(
    keys.flatMap((key) => {
      const value = obj[key];
      return value === undefined ? [] : [[key, value]];
    }),
  );
}

/** The renderer plugin for a weather row: each column's reading. */
export class ColumnWeatherRendererPlugin
  implements
    RenderPlugin<WeatherCell, SheetLegend.DefaultRow, ColumnWeatherRow>
{
  static readonly spec: PerTypeSpec = COLUMN_WEATHER_SPEC;
  static readonly ACTOR = new Actor("weather-plugin");
  static readonly rendererOf: (row: SheetRow.Joined.Handle) => Option<ColumnWeatherRow> = weatherRendererOf;
  readonly role = "renderer";
  readonly id = "column.weather";
  readonly aliases = ["weather"];
  readonly rowClass: typeof ColumnWeatherRow = ColumnWeatherRow;
  readonly icon: HTMLTemplateElement = LonaIcons.$template(CloudIcon);

  static compressWeatherData(data: Option<Json>): Option<Json> {
    const wd: Option<JsonObject> = Json.object(data ?? null);
    if (!wd) return data;
    const day = Json.child(wd, "day");
    if (!day) return data;

    return {
      ...presentKeys(wd, ["date", "source"]),
      day: presentKeys(day, [
        "avgtempF",
        "maxtempF",
        "mintempF",
        "avghumidity",
        "uv",
        "condition",
        "totalprecipIn",
      ]),
    };
  }

  #cellHandles = new Map<WeatherCell, { dispose(): void }>();
  #rowDateCache = new Map<
    string,
    Map<string, Option<WeatherCellData>>
  >();

  readonly #homeCardParts: ColumnWeatherRendererPlugin.HomeCardParts;
  readonly #locations: ColumnWeatherRendererPlugin.Locations;
  /** Whether a `getWeatherLocations()` load this plugin asked for is in flight. */
  #loading = false;

  /**
   * `locations`: the client whose weather-location table labels the rows
   * (`client.getWeatherLocations()`); until it has loaded, the labels come
   * from `WeatherLocations.FALLBACK`.
   */
  constructor(
    readonly makeRowOptions: RowOptionsElement.Factory,
    homeCardParts: ColumnWeatherRendererPlugin.HomeCardParts,
    locations: ColumnWeatherRendererPlugin.Locations,
  ) {
    this.#homeCardParts = homeCardParts;
    this.#locations = locations;
  }

  /**
   * The client's table, or `WeatherLocations.FALLBACK` while it has not
   * loaded (asking for it then: one load at a time, asked again after a
   * failure).
   */
  #table(): WeatherLocations {
    const loaded = this.#locations.peekWeatherLocations();
    if (loaded != null) return loaded;
    if (!this.#loading) {
      this.#loading = true;
      this.#locations.getWeatherLocations().then(
        () => {
          this.#loading = false;
        },
        (err: unknown) => {
          this.#loading = false;
          Tracing.warn("ui")("weather/locations-unloaded", { error: String(err) });
        },
      );
    }
    return WeatherLocations.FALLBACK;
  }

  /**
   * The location `row` is configured for (`weatherLocationIdOf`), in the
   * client's table — `WeatherLocations.FALLBACK` until that has loaded;
   * `null` when the row names no location (none is invented) or one the
   * table does not carry.
   */
  locationOf(row: SheetRow.Joined.Handle): Option<WeatherLocation> {
    const lid = weatherLocationIdOf(row);
    return lid == null ? null : this.#table().byId.get(lid) ?? null;
  }

  /**
   * Each face of the home card carries a slot holding what the row's location
   * calls for: its weather accessory, or — for a row that names no location —
   * the prompt that lets the user choose one (empty when the host provides no
   * prompt; no weather is requested for a location nobody chose). The list
   * face's slot is its accessory; the card face's is the last child of its
   * content, so the face cards mode shows carries the prompt too. `bind`
   * swaps both slots' parts when the row's location changes, so choosing one
   * shows its weather in place.
   */
  homeCardFactory: HomeCardFactory<ColumnWeatherRendererPlugin.HomeCardContent> = {
    make: (ctx) => {
      const $listSlot = makeAccessorySlot();
      const $cardSlot = makeAccessorySlot();
      const $cardContent = this.#homeCardParts.makeCardContent();
      $cardContent.appendChild($cardSlot);
      this.#cardSlots.set($cardContent, $cardSlot);
      this.#fillAccessorySlot($listSlot, ctx);
      this.#fillAccessorySlot($cardSlot, ctx);
      return {
        $cardContent,
        $listContent: null,
        $listAccessory: $listSlot,
      };
    },

    bind: (content, ctx) => {
      if (content.$listAccessory != null) this.#fillAccessorySlot(content.$listAccessory, ctx);
      const $cardSlot = this.#cardSlots.get(content.$cardContent);
      if ($cardSlot != null) this.#fillAccessorySlot($cardSlot, ctx);
      const raw = ctx.row.single()?.peek(ctx.datePartial)?.value as Option<WeatherCellData>;
      content.$cardContent.bind({ day: raw?.day ?? null });
    },
  };

  /** The accessory slot each home card's card face carries. */
  readonly #cardSlots = new WeakMap<HTMLElement, HTMLElement>();

  /** The location each home-card accessory slot currently shows (`null`: the prompt). */
  readonly #slotLocations = new WeakMap<HTMLElement, Option<number>>();

  /**
   * Fills `$slot` for `ctx.row`'s location unless it already shows that one.
   * When the part it replaces holds focus (the prompt the location dialog
   * returned focus to), the new part takes it, so focus does not fall to
   * nowhere.
   */
  #fillAccessorySlot($slot: HTMLElement, ctx: HomeCardFactory.Context): void {
    const lid = weatherLocationIdOf(ctx.row);
    if (this.#slotLocations.has($slot) && this.#slotLocations.get($slot) === lid) return;
    this.#slotLocations.set($slot, lid);
    const row = ctx.row;
    const $part = lid != null
      ? this.#homeCardParts.makeListAccessory({ lid, date: ctx.localDate })
      : this.#homeCardParts.makeLocationPrompt?.({ onChoose: () => ctx.chooseLocation?.(row) }) ?? null;
    const $old = $slot.firstElementChild;
    const hadFocus = $old != null && $old.matches(":focus-within");
    if ($part == null) $slot.replaceChildren();
    else $slot.replaceChildren($part);
    if (hadFocus && $part != null) {
      if (!$part.hasAttribute("tabindex")) $part.setAttribute("tabindex", "-1");
      $part.focus();
    }
  }

  optionsInfo(_row: SheetRow.Joined.Handle): Option<string> {
    return "Track the weather for a given day, week or month.";
  }

  makeCell(): WeatherCell {
    return WeatherCell.make();
  }

  async bindCell(
    $cell: WeatherCell,
    { row, sheetRow, column, handle }: BindCellContext<ColumnWeatherRow>,
  ): Promise<void> {
    this.#disposeHandleForCell($cell);

    const time = column.discrete();
    const dtrange = time[0].toRange();
    const dates = dtrange.iter();
    const rowId = sheetRow.id;
    const rowCache = this.#getRowCache(sheetRow.id.toString());
    const firstDate = nonnull(dates[0], "a weather column spans at least one date");
    const lastDate = nonnull(dates[dates.length - 1], "a weather column spans at least one date");
    const range = new NaiveDate.Range(firstDate, lastDate.addDays(1));
    const TOOLTIP_ID = "__weather-tooltip";

    // A reading is keyed by its day: a date-keyed cell's own, a timepoint
    // cell's start day (an alias row's source stores datetime-keyed
    // readings). A date-keyed cell wins its day.
    const bindWithData = (cells: readonly SheetRow.Cell[], loading: boolean) => {
      const cellsByDse = new Map<number, SheetRow.Cell>();
      for (const c of cells) if (c.kind === "timepoint") cellsByDse.set(c.dtr.start.date.dse, c);
      for (const c of cells) if (c.kind === "date") cellsByDse.set(c.date.start.dse, c);

      const weathers = dates.map((date) => {
        const cell = cellsByDse.get(date.dse);
        const weatherData = cell?.value as Option<WeatherCellData>;
        if (weatherData != null) rowCache.set(date.toString(), weatherData);
        const cached = rowCache.get(date.toString()) ?? null;
        const day = (weatherData ?? cached)?.day ?? null;
        return { date, weather: day, loading };
      });

      $cell.bind({
        weathers,
        showTooltip: ({ date, weather }) => {
          const key = ColumnIndex.Key.fromDate(date, "lower");
          const resolved =
            handle.rebind.sheet.indexRenderer.mapper.resolveKey(key);
          const { subindex } =
            handle.rebind.sheet.indexRenderer.mapper.indexOfKey(key);
          const $tt = WeatherTooltip.instance.bind(weather);
          handle.tooltip.showTooltip(
            TOOLTIP_ID,
            $tt,
            {
              x: new ColumnIndex.Key.Resolved.WithSubindex(resolved, subindex),
              y: rowId,
            },
            "bottom",
            { margin: { top: 2, left: 0, right: 0, bottom: 0 } },
          );
        },
        hideTooltip: () => handle.tooltip.hideTooltip(TOOLTIP_ID),
      });
    };

    const lid = row.lid;

    // Relocation-repaint diagnostics (session breadcrumb): proves bindCell
    // re-ran after a structural rebind, and against WHICH joined row/key —
    // the stale-paint bug class is this NOT re-running (or running against
    // the old row) after a weather relocation re-keys the subtree.
    Tracing.log("weather/bind-cell", {
      row: sheetRow.id.toString(),
      lookupKey: sheetRow.lookupKey ?? null,
      lid: lid ?? null,
    });

    const h = sheetRow.cells(range);
    this.#cellHandles.set($cell, h);
    h.onLoading(() => {
      // The row is fetching data for a (possibly newly re-keyed) location — drop
      // the stale cache and shimmer the cells until the new data arrives, rather
      // than flashing blank or leaving the previous location's readings on screen.
      rowCache.clear();
      bindWithData([], true);
    });
    h.onData((cells) => {
      Tracing.log("weather/bind-data", {
        row: sheetRow.id.toString(),
        lookupKey: sheetRow.lookupKey ?? null,
        cells: cells.length,
      });
      bindWithData(cells, false);
    });
  }

  #getRowCache(rowId: string): Map<string, Option<WeatherCellData>> {
    const existing = this.#rowDateCache.get(rowId);
    if (existing) return existing;
    const created = new Map<string, Option<WeatherCellData>>();
    this.#rowDateCache.set(rowId, created);
    return created;
  }

  #disposeHandleForCell($cell: WeatherCell) {
    this.#cellHandles.get($cell)?.dispose();
    this.#cellHandles.delete($cell);
  }

  onCellRecycled($cell: WeatherCell) {
    this.#disposeHandleForCell($cell);
  }

  legendFactory: SheetLegendClass<SheetLegend.DefaultRow> = weatherLegendClass(
    (row) => this.legendLabel(row),
  );

  /**
   * The label follows the binding (row-subsumption.md rule 2): a renderer bound
   * to data is labelled by that data's location; one bound to no data by its
   * own label, else its kind — never a location it does not show. A weather
   * alias with no renderer left is labelled by the location it is configured
   * for, else its own label or its kind (never the default location).
   */
  legendLabel(row: SheetRow.Joined.Handle): string {
    const renderer = weatherRendererOf(row);
    if (renderer != null && !renderer.isBound) return row.label ?? row.wireType;
    if (renderer == null) {
      const variant = liveVariantOf(row);
      if (variant instanceof AliasRow && weatherLocationIdOf(row) == null) return row.label ?? variant.displayName;
    }
    return this.locationOf(row)?.label ?? "Untitled";
  }

  onResize() {}
  loadData = null;
  onBindRow() {}
  onViewportColumnsChanged() {}
  onRenderedColumnsWillChange() {}
}

/** The weather legend, labelling a row with `labelOf(row)`. */
function weatherLegendClass(
  labelOf: (row: SheetRow.Joined.Handle) => string,
): SheetLegendClass<SheetLegend.DefaultRow> {
  return class {
    static make(): SheetLegend.DefaultRow {
      return SheetLegend.DefaultRow.make();
    }

    static bind(
      $legend: SheetLegend.DefaultRow,
      row: SheetRow.Joined.Handle,
      handle: SheetElement.Handle,
      sheetTimescale: Timescale,
    ): void {
      $legend.bind(
        row,
        (target) => handle.rebind.revealRowOptionsPopover(row, target),
        handle,
        {
          defaultLabel: labelOf(row),
          sheetTimescale,
        },
      );
    }

    static computeWidth(row: SheetRow.Joined.Handle, measure: TextMeasurer, sheetTimescale: string): number {
      const label = row.label ?? labelOf(row);
      const timescaleVisible = sheetTimescale !== row.timescaleSetting;
      return DefaultLegendLayout.width({ label, timescaleVisible }, measure);
    }
  };
}

/** An empty, layout-transparent slot for a home card face's accessory part. */
function makeAccessorySlot(): HTMLElement {
  const $slot = document.createElement("span");
  $slot.style.display = "contents";
  return $slot;
}

/**
 * Types that go with `ColumnWeatherRendererPlugin`: `HomeCardContent`,
 * `HomeCardParts`, `Locations`, `Attributes`.
 */
export namespace ColumnWeatherRendererPlugin {
  export interface HomeCardContent extends HTMLElement {
    bind(props: { day: Option<WeatherTooltip.Day> }): this;
  }

  export interface HomeCardParts {
    /**
     * The card face. It shows its children: the plugin appends the slot
     * holding the row's accessory or prompt (as on the list face) last.
     */
    makeCardContent(): HomeCardContent;
    makeListAccessory(props: { lid: number; date: NaiveDate }): HTMLElement;
    /**
     * The accessory for a row that names no location: a control reading
     * "Choose a location" that calls `onChoose` once per activation. A host
     * that cannot reveal a row's options leaves it out: such a card draws no
     * accessory.
     */
    makeLocationPrompt?(props: { onChoose: () => void }): HTMLElement;
  }

  /**
   * Where the plugin reads its weather-location table: the client
   * (`LonaSdk.Client` carries both).
   */
  export type Locations = Pick<LonaSdk.Client, "getWeatherLocations" | "peekWeatherLocations">;

  export type Attributes = {
    lid?: number;
    nestedRows?: ParentStoredRow.NestedRowsAttribute;
  };
}
